import { useCallback, type ReactNode } from 'react'
import { type EntityId, SystemCloserStrategyTypeSchema, type SystemCloserStrategy } from '@terros/common'
import { notifications } from '@mantine/notifications'
import { NumberInput, Select, Stack } from '@mantine/core'
import { useToastError } from '@hooks'
import { SYSTEM_CLOSER_STRATEGY_ITEMS, useUpdatePreference } from '@front/model'
import { KpiSingleSelect } from '../stats/KpiSelect'
import { PreferenceInput } from './PreferenceInput'
import { usePreferenceContext } from './Context'

type Props = {
  targetId: EntityId
}

export const PreferenceSystemStrategy = ({ targetId }: Props): ReactNode => {
  return (
    <PreferenceInput targetId={targetId} preference='systemCloserStrategy'>
      {({ data, disabled }) => (
        <StrategyFields value={data.current ?? data.inherited} disabled={disabled} targetId={targetId} />
      )}
    </PreferenceInput>
  )
}

type StrategyFieldsProps = {
  value: SystemCloserStrategy
  disabled: boolean
  targetId: EntityId
}

const StrategyFields = ({ value, disabled, targetId }: StrategyFieldsProps): ReactNode => {
  const { isAllowedUpdate } = usePreferenceContext()
  const { update, error } = useUpdatePreference('systemCloserStrategy', targetId)
  useToastError(error)
  const isDisabled = disabled || !isAllowedUpdate

  const save = useCallback(
    async (strategy: SystemCloserStrategy): Promise<void> => {
      await update(strategy)
      notifications.show({ message: 'Preference saved', color: 'green' })
    },
    [update]
  )

  const handleTypeChange = useCallback(
    async (selected: string | null): Promise<void> => {
      const parsed = SystemCloserStrategyTypeSchema.safeParse(selected)
      if (!parsed.success) return
      switch (parsed.data) {
        case 'random':
          return save({ type: 'random' })
        case 'balanced':
          return save({ type: 'balanced' })
        case 'preferential':
          return save({ type: 'preferential', jitter: 0 })
        case 'performance':
          return save({ type: 'performance', kpi: 'event.closes', days: 30, jitter: 0 })
      }
    },
    [save]
  )

  const handleWindowDaysChange = useCallback(
    async (days: string | number): Promise<void> => {
      if (typeof days === 'string') return
      if (value.type !== 'balanced') return
      await save({ type: 'balanced', windowDays: days })
    },
    [save, value]
  )

  const handleJitterChange = useCallback(
    async (jitter: string | number): Promise<void> => {
      if (typeof jitter === 'string') return
      if (value.type === 'preferential') return save({ type: 'preferential', jitter })
      if (value.type === 'performance') return save({ ...value, jitter })
    },
    [save, value]
  )

  const handleKpiChange = useCallback(
    async (kpi: string): Promise<void> => {
      if (value.type !== 'performance') return
      await save({ ...value, kpi })
    },
    [save, value]
  )

  const handleDaysChange = useCallback(
    async (days: string | number): Promise<void> => {
      if (typeof days === 'string') return
      if (value.type !== 'performance') return
      await save({ ...value, days })
    },
    [save, value]
  )

  return (
    <Stack gap='xs'>
      <Select
        data={SYSTEM_CLOSER_STRATEGY_ITEMS}
        disabled={isDisabled}
        value={value.type}
        onChange={handleTypeChange}
      />
      {value.type === 'balanced' && (
        <NumberInput
          label='Window (days)'
          description='Number of days to look ahead when balancing event counts'
          disabled={isDisabled}
          min={1}
          max={30}
          value={value.windowDays ?? 7}
          onChange={handleWindowDaysChange}
        />
      )}
      {value.type === 'preferential' && (
        <NumberInput
          label='Jitter (%)'
          description='Chance of picking randomly instead of by rank. 0 = always use rank.'
          disabled={isDisabled}
          min={0}
          max={100}
          value={value.jitter}
          onChange={handleJitterChange}
        />
      )}
      {value.type === 'performance' && (
        <>
          <KpiSingleSelect label='Metric' value={value.kpi} onChange={handleKpiChange} disabled={isDisabled} />
          <NumberInput
            label='Lookback (days)'
            description='How many days of past performance to evaluate'
            disabled={isDisabled}
            min={1}
            max={365}
            value={value.days}
            onChange={handleDaysChange}
          />
          <NumberInput
            label='Jitter (%)'
            description='Chance of picking randomly instead of by performance. 0 = always use performance.'
            disabled={isDisabled}
            min={0}
            max={100}
            value={value.jitter}
            onChange={handleJitterChange}
          />
        </>
      )}
    </Stack>
  )
}
